import {
  Component,
  Input,
  Output,
  EventEmitter as AngularEventEmitter,
  ViewChild,
} from '@angular/core';
import { EnvVariables } from '../../environments/environment';
import * as debug from 'debug';
import * as _ from 'lodash';
import * as $ from 'jquery';
import 'moment-timezone';
import * as moment from 'moment';
import * as turf from '@turf/turf';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl.js';
import { EventEmitter } from '../../includes/eventEmitter';
import { GeolocationProvider } from '../../providers/geolocation/geolocation';
import { ToolsProvider } from '../../providers/tools/tools';
import { UserProvider } from '../../providers/user/user';
import { ConfigProvider } from '../../providers/config/config';
import { NativeAccessProvider } from '../../providers/native-access/native-access';
import { Platform, ModalController, NavParams } from 'ionic-angular';
import { CrudApiProvider } from '../../providers/crud-api/crud-api';
import { UserVerificationProvider } from '../../providers/user-verification/user-verification';
import { UiToolsProvider } from '../../providers/ui-tools/ui-tools';

let activeMap;

// Marker interfaces
export interface IGeometry {
  type: string;
  coordinates: number[];
}
export interface IFeature {
  type: string;
  geometry: IGeometry;
  properties?: any;
  $key?: string;
}
export interface IGeoJson {
  type: string;
  features: IFeature[];
}

@Component({
  selector: 'discover-map',
  templateUrl: 'discover-map.html',
})
export class DiscoverMapComponent extends EventEmitter {
  log = debug('app:this.discoverMapComponent');

  // Use these variables to prevent mapbox from firing up every visit
  static staticMapDiv;
  static staticMap;

  @ViewChild('container')
  discoverMapContainer;
  @Input()
  style: string = 'NAVIGATION'; // style for the map

  @Output()
  activeMarker = new AngularEventEmitter(); // send the active marker to the parent
  @Output()
  load = new AngularEventEmitter(); // send the current load status to the parent
  @Output()
  userStatusSet = new AngularEventEmitter();
  @Output()
  clusterClicked = new AngularEventEmitter();
  @Output()
  onDrag = new AngularEventEmitter();
  @Output()
  onZoom = new AngularEventEmitter();
  @Output()
  mapClick = new AngularEventEmitter();
  @Output()
  denyLocation = new AngularEventEmitter();
  @Output()
  closedDrawer = new AngularEventEmitter();

  waiting = false;
  loaded = false;
  inited = false;
  isAndroid = false;
  hasBeenDisplayed: boolean;
  mapId: string;
  hasLoadedGeolocation: boolean;
  expirationSet: boolean;
  events = [];
  uniqueId = _.uniqueId();
  currentLocationPref: string;
  loadedMapData = [];

  // error messages
  errorMessage = '';
  isAndroidError = '';
  isntAndroidError = '';

  // mapbox variables
  map: mapboxgl.Map;
  styles = {
    NAVIGATION: 'mapbox://styles/cdllife/ck8ypuc5n03rw1it34su7yn10',
    SATELLITE: 'mapbox://styles/cdllife/ck8ypn60j03k11hpcvj7vxg29',
  };
  userLocationMarker: mapboxgl.Marker;
  meetGreetMarker: mapboxgl.Marker;
  storeActiveMarker: mapboxgl.Marker;
  geoMarkers: IGeoJson = {
    type: 'FeatureCollection',
    features: [],
  };

  // store marker data
  nearbyUsers: IFeature[];
  nearbyFriends: IFeature[];
  markers = [];
  markersOnScreen = {};

  activeMapFilter = 'everyone';
  mapFilters = [
    { title: 'Everyone', value: 'everyone', icon: 'globe' },
    { title: 'Friends', value: 'friends', icon: 'users' },
    { title: 'Meet & Greets', value: 'meet-greet', icon: 'handshake-alt' },
  ];

  constructor(
    public tools: ToolsProvider,
    private env: EnvVariables,
    public geolocation: GeolocationProvider,
    public plt: Platform,
    public configs: ConfigProvider,
    public user: UserProvider,
    public crudApi: CrudApiProvider,
    public nativeAccess: NativeAccessProvider,
    public userVerification: UserVerificationProvider,
    public modalCtrl: ModalController,
    public uiTools: UiToolsProvider,
    public navParams: NavParams
  ) {
    super();

    //set the current map
    this.mapId = _.uniqueId();
    if (activeMap) {
      try {
        activeMap.clearMap();
      } catch (e) {}
    }
    activeMap = window['activeMap'] = this;
    this.log('discover_map_layers | constructed', this.uniqueId);
    // add the mapbox token
    mapboxgl.accessToken = this.env.mapboxglAccessToken;
    // check if the users device is an android
    this.isAndroid = this.plt.is('android');
  }

  /**
   * Begin navigation
   */
  ngOnInit() {
    this.log('discover_map_layers | inited', this.uniqueId);
    this.clearMap();
    this.expirationSet = !!this.user.get('recentStatus.statusExpirationDate');

    if (this.navParams.get('type')) {
      this.activeMapFilter = this.navParams.get('type');
    }

    setTimeout(() => {
      this.buildMap(); // defer until stack is clear
    }, 1000);

    setTimeout(() => {
      this.waiting = true;
    }, 750);

    setTimeout(() => {
      this.displayErrorMessages();
    }, 5000);

    // reInit map
    this.events.push(
      this.geolocation.on('updateUserLocation', () => {
        this.updateUserLocationMarker();
      })
    );

    // set resize events
    this.events.push(
      this.tools.on('viewDidEnter', () => {
        this.resizeMapTimeouts();
      })
    );

    this.events.push(
      this.tools.on('viewWillEnter', () => {
        this.resizeMapTimeouts();
      })
    );

    this.events.push(
      this.tools.on('onKeyboardShow', () => {
        this.resizeMapTimeouts();
      })
    );

    this.events.push(
      this.tools.on('onKeyboardHide', () => {
        this.resizeMapTimeouts();
      })
    );

    this.events.push(
      this.tools.on('orientationChange', () => {
        this.resizeMapTimeouts();
      })
    );
  }

  /**
   * Clean up
   */
  ngOnDestroy() {
    this.log('discover_map_layers | destroy', this.uniqueId);
    if (activeMap === this) {
      this.clearMap();
    } else {
      this.log('discover_map_layers | skipping destroy');
    }

    _.each(this.events, e => {
      e();
    });
  }

  /**
   * Build the map source and layers
   */
  buildMap() {
    const offset = this.configs.get('activeStatus.value'),
      type = this.configs.get('activeStatus.type'),
      cutoff = moment().subtract(offset, type);
    let myLng, myLat;

    // get the most recent position
    this.geolocation.getRecentPosition().then(async position => {
      myLng = _.get(position, 'longitude');
      myLat = _.get(position, 'latitude');

      // const pref = this.user.get('userLocationPref');

      // this was causing a bug where if a user set them selves to private this modal would continually set the user to friends, even if the front end said they were set to private  commented out for now, this could possible affect meet and greets
      // if (pref === 'nobody') {
      //   if (
      //     await this.uiTools.actionSheetPrompt({
      //       title: 'Turn on location services?',
      //       description: `<div>Location services are required for several CDLLife app features.</div>
      //           <div class="cm-Margin--small-top">We'll use your location to show posts in the <b>Nearby Feed</b>, to show friends in <b>Discover</b>, and to power the <b>Places & Navigation</b> features within the Services tab.</div>
      //           <div class="cm-Margin--small-top">We will only use your location in the background when you are actively using the  <b>Trip Planner</b> for GPS navigation. </div>`,
      //       button: 'Continue',
      //       cancel: '',
      //     })
      //   ) {
      //     // this.user.set('userLocationPref', 'friends');
      //     // this.currentLocationPref = this.user.get('userLocationPref');
      //     this.user
      //       .crudUpdate({
      //         userLocationPref: this.currentLocationPref,
      //       })
      //       .catch(e => {
      //         this.log(e);
      //       });
      //     this.addUsersLocation();
      //     if (this.user.get('recentStatus.statusExpirationDate'))
      //       this.createMeetGreetMarker(myLng, myLat);
      //   } else {
      //     this.denyLocation.emit();
      //   }
      // }

      let mapDiv = (DiscoverMapComponent.staticMapDiv =
          document.createElement('div')),
        defaultMapConfig = {
          container: mapDiv,
          style: this.styles.NAVIGATION,
          center: [myLng || -94.575522, myLat || 39.0997],
          zoom: 9,
          minZoom: 1,
          maxZoom: 19,
          pitch: 0,
          antialias: true,
        };

      // append the map element to the container
      this.discoverMapContainer.nativeElement.append(mapDiv);
      window['_map'] = this.map;

      // create map
      this.map =
        DiscoverMapComponent.staticMap =
        window['_map'] =
          new mapboxgl.Map(defaultMapConfig);

      // set the active map component
      this.map.activeComponent = this;

      // delay map zoom
      this.map.on(
        'zoom',
        _.debounce(() => {
          DiscoverMapComponent.staticMap.activeComponent.onZoom.emit();
        }, 400)
      );

      // delay map move
      this.map.on(
        'move',
        _.debounce(e => {
          DiscoverMapComponent.staticMap.activeComponent.onDrag.emit({
            from: this.map.lastCenter,
            to: (this.map.lastCenter = this.map.getCenter()),
          });
        }, 400)
      );

      this.map.on('dragstart', () => {
        this.loaded && this.closedDrawer.emit(true);
      });

      this.centerMap(myLng || -94.575522, myLat || 39.0997);

      this.map.on('load', async () => {
        // const ac = DiscoverMapComponent.staticMap.activeComponent;
        this.populateMarkers(myLng, myLat);
        this.addBaseSources();
        if (position) this.addUsersLocation();
        if (position && this.user.get('recentStatus.statusExpirationDate'))
          this.createMeetGreetMarker(myLng, myLat);

        // disable map rotation using touch rotation gesture
        this.map.touchZoomRotate.disableRotation();

        // clusters
        this.map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'nearbyUsers',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#f4f5f8',
            'circle-stroke-width': 3,
            'circle-stroke-color': '#fff',
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              20,
              100,
              30,
              750,
              40,
            ],
            'circle-blur': 0.1,
          },
        });

        this.map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'nearbyUsers',
          filter: ['has', 'point_count'],
          layout: {
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 18,
            'text-field': '{point_count_abbreviated}',
          },
          paint: {
            'text-color': '#3880ff',
          },
        });

        // individual markers
        this.map.addLayer({
          id: 'unclustered-point',
          type: 'symbol',
          source: 'nearbyUsers',
          filter: ['!', ['has', 'point_count']],
        });

        this.map.on('click', 'clusters', e => {
          e.preventDefault();

          this.log('CLICKED cluster');
          let features = this.map.queryRenderedFeatures(e.point, {
            layers: ['clusters'],
          });
          let clusterId = features[0].properties.cluster_id,
            pointCount = features[0].properties.point_count,
            clusterSource = this.map.getSource('nearbyUsers'),
            nearbyFriends = [],
            otherNearbyUsers = [];

          // get the cluster children
          clusterSource.getClusterLeaves(
            clusterId,
            pointCount,
            0,
            (err, features) => {
              _.each(features, feature => {
                _.get(feature.properties.friend, 'acceptedFriend')
                  ? nearbyFriends.push(feature)
                  : otherNearbyUsers.push(feature);
              });
            }
          );

          // Pass the users in the cluster to the list modal
          this.openMapListView(nearbyFriends, otherNearbyUsers, true);
        });

        // once the map has been fully rendered, create the clusters
        this.map.on('render', () => {
          if (!this.map.isSourceLoaded('nearbyUsers')) return;
          this.updateMarkers();
        });

        this.hasBeenDisplayed = true;
        this.load.emit();
        this.loaded = true;
      });

      $(mapDiv).on('click', e => {
        this.closedDrawer.emit(true);
        e.preventDefault();
        e.stopPropagation();
        if (
          $(e.target).hasClass('MapMarker') ||
          $(e.target).hasClass('user-status-actions')
        ) {
          return;
        }
        this.log('map clicked');

        $('.user-status-actions').css('display', 'none');
        DiscoverMapComponent.staticMap.activeComponent.mapClick.emit();
        DiscoverMapComponent.staticMap.activeComponent.inactivateMarker();
      });
    });
  }

  /**
   * Fetch markers from API
   */
  async populateMarkers(myLng = -94.575522, myLat = 39.0997, reload = true) {
    let tmpFeatures = [];

    if (reload || !this.loadedMapData) {
      try {
        this.loadedMapData =
          _.get(await this.crudApi.get('/api/users/map'), 'data') || [];
      } catch (e) {
        this.log('error loading map data', e);
        this.loadedMapData = [];
      }
    }

    _.each(this.loadedMapData, user => {
      const lastActive = _.get(user, 'recentStatus.lastReportedActive'),
        userCoords = this.checkMeetGreetLoc(
          _.get(user, 'recentStatus.nextLocation'),
          {
            longitude: _.get(user, 'recentStatus.longitude'),
            latitude: _.get(user, 'recentStatus.latitude'),
          }
        ),
        locationPref = _.get(user, 'userLocationPref') || 'nobody',
        acceptedFriend = _.get(user, 'acceptedFriend'),
        userId = _.get(user, '_id'),
        username = _.get(user, 'userNamePretty') || _.get(user, 'username');

      // return cases
      if (
        !_.includes(['everyone', 'anyone', 'friends'], locationPref) ||
        !userCoords ||
        !lastActive ||
        !username ||
        (this.activeMapFilter === 'friends' && !acceptedFriend) ||
        (this.activeMapFilter === 'meet-greet' &&
          !_.get(user, 'recentStatus.statusExpirationDate'))
      ) {
        return;
      }

      // set distance away from marker
      if (
        locationPref === 'anyone' ||
        (acceptedFriend && locationPref !== 'nobody')
      ) {
        if (myLng && myLat) {
          user.distanceAway = turf.distance(
            turf.point([myLng, myLat]),
            turf.point(userCoords),
            { units: 'miles' }
          );
        }
      }

      tmpFeatures.push({
        type: 'Feature',
        properties: {
          name: username,
          friend: user,
          userId: userId,
          lastUpdated: moment(lastActive).fromNow(),
          acceptedFriend: acceptedFriend,
          distanceAway: _.get(user, 'distanceAway'),
          userImage: _.get(user, 'userImage', 'assets/imgs/default-avatar.png'),
        },
        geometry: {
          type: 'Point',
          coordinates: userCoords,
        },
      });
    });
    this.geoMarkers.features = tmpFeatures;

    // add nearby users
    if (reload) {
      this.nearbyUsers = _.filter(this.geoMarkers.features, user => {
        let props = user.properties;
        return (
          'distanceAway' in props &&
          !_.get(props, 'acceptedFriend') &&
          _.get(props, 'distanceAway') <= 100
        );
      });
      this.nearbyUsers = _.sortBy(this.nearbyUsers, 'properties.distanceAway');

      // add nearby friends
      this.nearbyFriends = _.filter(this.geoMarkers.features, user => {
        let props = user.properties;
        return (
          'distanceAway' in props &&
          _.get(props, 'acceptedFriend') &&
          _.get(props, 'distanceAway') <= 100
        );
      });
      this.nearbyFriends = _.sortBy(this.nearbyFriends, [
        'properties.distanceAway',
      ]);
    }

    // if it's already been inited then replace the source data
    if (this.map.isSourceLoaded('nearbyUsers')) {
      this.map.getSource('nearbyUsers').setData(this.geoMarkers);
      this.updateMarkers();
    }
    this.inited = true;
  }

  /**
   * Create custom HTML markers for every cluster on screen
   */
  updateMarkers(): void {
    const features = this.map.querySourceFeatures('nearbyUsers');
    let newMarkers = {};
    const existingMarkerLookup = _.keyBy(
      this.markers,
      '_feature.properties.name'
    );

    for (let i = 0; i < features.length; i++) {
      let coords = features[i].geometry.coordinates,
        props = features[i].properties;

      // don't create a custom marker for clusters
      if (props.cluster) continue;

      const name = _.get(features[i], 'properties.name');

      let m = existingMarkerLookup[name];
      if (!m) {
        const el = this.createSingleMarker(features[i]);
        m = existingMarkerLookup[name] = new mapboxgl.Marker({
          element: el,
          anchor: 'center',
        }).setLngLat(coords);
      } else {
        // if the marker already exists, then we need to check if a meet & greet
        // has been created.
        let tmpFriend = JSON.parse(_.get(props, 'friend'));
        if (
          (_.get(tmpFriend, 'recentStatus.statusExpirationDate') &&
            !$(m._element).find('.user-Status-icon').length) ||
          (!_.get(tmpFriend, 'recentStatus.statusExpirationDate') &&
            $(m._element).find('.user-Status-icon').length)
        ) {
          delete existingMarkerLookup[name];
          delete this.markersOnScreen[name];
          m.remove();
          const el = this.createSingleMarker(features[i]);
          m = existingMarkerLookup[name] = new mapboxgl.Marker({
            element: el,
            anchor: 'center',
          }).setLngLat(coords);
        }
      }
      newMarkers[name] = m;
      if (!this.markersOnScreen[name]) {
        m.addTo(this.map);
        m._feature = features[i];
        this.markers.push(m);
      }
    }

    for (let name in this.markersOnScreen) {
      if (!newMarkers[name]) this.markersOnScreen[name].remove();
    }
    this.markersOnScreen = newMarkers;
  }

  /**
   * Change the data source when the map filter changes
   * @param value current map filter
   */
  changeMapFilter(value: string): void {
    if (this.activeMapFilter === value) return;
    this.activeMapFilter = value;

    this.geolocation.getRecentPosition().then(async position => {
      let myLng = _.get(position, 'longitude');
      let myLat = _.get(position, 'latitude');

      this.populateMarkers(myLng, myLat, false);
      this.updateMarkers();
    });

    this.tools.logAnalyticsEvent('trackCustom', 'discover-map-change-filter', {
      userId: this.user.get('_id'),
      filter: value,
    });
  }

  /**
   * Create an HTML marker for a single user
   * @param props geojson feature props
   */
  createSingleMarker(feature) {
    let el = document.createElement('span'),
      props = feature.properties,
      markerImage = _.get(props, 'userImage'),
      markerIcon = _.get(feature, 'properties.icon', 'fal fa-user-circle'),
      friendData = JSON.parse(_.get(props, 'friend'));

    el.classList.add('MapMarker');

    // add user image or fallback to an icon
    if (markerImage) {
      if (/d2z5cdom2mbuj5/.test(markerImage)) {
        markerImage = markerImage + '.resize.jpg?width=75';
      } else if (/=large/.test(markerImage)) {
        markerImage = markerImage.replace(/=large/, '=small');
      }

      el.innerHTML = `
            <div class="MapMarker-imageWrapper">
              <img src="${markerImage}" />
            </div>
          `;
    } else {
      el.innerHTML = `
        <div class="MapMarker-imageWrapper">
          <img src="assets/imgs/other-svg/default-avatar.svg" />
        </div>
      `;
    }

    // add active meet & greet indicator
    if (_.get(friendData, 'recentStatus.statusExpirationDate')) {
      el.innerHTML += `
        <span class="user-Status-icon">
          <i class="fas fa-handshake-alt" cm-color="Neutral / White"></i>
        </span>
      `;
    }

    // create click event for a single marker
    el.addEventListener('click', async e => {
      this.log('CLICKED single user');

      this.tools.logAnalyticsEvent('trackCustom', 'discover-map-click-marker', {
        userId: this.user.get('_id'),
      });

      e.preventDefault();
      e.stopPropagation();

      if (!(await this.userVerification.isVerified())) {
        return;
      }

      this.storeActiveMarker = el;

      Object.defineProperty(feature, '_markerElement', {
        value: el,
        writable: false,
        enumerable: false,
      });

      this.activeMarker.emit(feature);
      this.emit('activeMarker', feature);
    });

    return el;
  }

  /**
   * Create a single marker for the current users meet&Greet
   * @param myLng users longitude
   * @param myLat users latitude
   * @returns
   */
  createMeetGreetMarker(myLng: number, myLat: number): void {
    let el = document.createElement('span'),
      props = this.user.info,
      markerImage = _.get(props, 'userImage'),
      distanceAway = turf.distance(
        turf.point([myLng, myLat]),
        turf.point([
          _.get(props, 'recentStatus.nextLocation.nextLng'),
          _.get(props, 'recentStatus.nextLocation.nextLat'),
        ]),
        { units: 'miles' }
      );
    el.classList.add('MapMarker');

    // if the meet&greet is within a quarter mile of your location,
    // then we don't need to show two markers.
    if (distanceAway <= 0.25) return;

    if (this.meetGreetMarker) {
      this.meetGreetMarker.remove();
    }

    // add user image
    if (markerImage) {
      if (/d2z5cdom2mbuj5/.test(markerImage)) {
        markerImage = markerImage + '.resize.jpg?width=75';
      } else if (/=large/.test(markerImage)) {
        markerImage = markerImage.replace(/=large/, '=small');
      }

      el.innerHTML = `
        <div class="MapMarker-imageWrapper">
          <img src="${markerImage}" />
        </div>
      `;
    } else {
      el.innerHTML = `
        <div class="MapMarker-imageWrapper">
          <img src="assets/imgs/other-svg/default-avatar.svg" />
        </div>
      `;
    }

    // add active meet & greet indicator
    el.innerHTML += `
      <span class="user-Status-icon">
        <i class="fas fa-handshake-alt" cm-color="Neutral / White"></i>
      </span>
    `;

    // add to the map
    this.meetGreetMarker = new mapboxgl.Marker({
      element: el,
      anchor: 'bottom',
    })
      .setLngLat([
        this.user.get('recentStatus.nextLocation.nextLng'),
        this.user.get('recentStatus.nextLocation.nextLat'),
      ])
      .addTo(this.map);

    // create the click event for the meet & greet
    el.addEventListener('click', e => {
      this.log('CLICKED my meet&greet');
      e.preventDefault();
      e.stopPropagation();

      this.userStatusSet.emit(true);
    });

    this.tools.logAnalyticsEvent(
      'trackCustom',
      'discover-map-create-meetgreet-marker',
      {
        userId: this.user.get('_id'),
      }
    );
  }

  /**
   * Add the base source to the map
   */
  addBaseSources() {
    try {
      if (!this.map.getSource('nearbyUsers')) {
        // Add a new source from our GeoJSON data and
        // set the 'cluster' option to true. GL-JS will
        // add the point_count property to your source data.
        this.map.addSource('nearbyUsers', {
          type: 'geojson',
          // Point to GeoJSON data.
          data: this.geoMarkers,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50, // Radius of each cluster when clustering points (defaults to 50)
        });
      }
    } catch (e) {}
  }

  /**
   * Clear markers from the map
   */
  clearMarkers() {
    this.inactivateMarker();
    if (_.size(this.markers)) {
      _.each(this.markers, m => {
        m.remove();
      });
    }

    this.markers = [];
  }

  /**
   * unbind all data to this marker
   */
  inactivateMarker() {
    this.activeMarker.emit(null);
    this.storeActiveMarker = null;
  }

  /**
   * Check if the current map is equal to our saved map
   */
  isActiveMapComponent() {
    return _.get(this.map, 'activeComponent') === this;
  }

  /**
   * Clear map of all markers
   */
  clearMap() {
    //this.clearMarkers();

    this.log('discover_map_layers | clearing map');

    if (this.userLocationMarker) {
      this.userLocationMarker.remove();
    }

    if (this.meetGreetMarker) {
      this.meetGreetMarker.remove();
    }
  }

  updateSource(source, data) {
    this.geoMarkers = data;
    this.map.getSource(source).setData(this.geoMarkers);
  }

  /**
   * Center the map on the users current location
   * @param includeCurrentPosition whether or not to inlcude the current users position
   */
  centerMap(lng: number, lat: number) {
    let bounds = new mapboxgl.LngLatBounds();
    this.log('center discover map markers: ', bounds);
    if (lng && lat) {
      bounds.extend([lng, lat]);
    }
    this.map.fitBounds(bounds, { padding: 80, maxZoom: 10 });
  }

  /**
   * Check if our saved map is the current map instance
   */
  isActiveComponent() {
    return _.get(this.map, 'activeComponent') === this;
  }

  /**
   * Checks if there are any null or empty values in the status
   * @returns true if all values are set, false if even one value is null
   */
  checkStatusFields() {
    // The only status values we are worried about
    let status = {
      msg: this.user.get('recentStatus.statusMessage'),
      loc: this.user.get('recentStatus.nextLocation'),
      eta: this.user.get('recentStatus.statusEta'),
      exp: this.user.get('recentStatus.statusExpirationDate'),
    };

    return !Object.keys(status).some(
      k => status[k] === '' || status[k] === null
    );
  }

  /**
   * Add the users location to the map
   */
  addUsersLocation() {
    const userPin = document.createElement('span');

    userPin.className = 'user-pin-wrapper status';

    if (this.user.get('userLocationPref') !== 'nobody') {
      return this.geolocation.getRecentPosition().then(
        position => {
          this.log('got users location', position);

          if (this.userLocationMarker) {
            this.userLocationMarker.remove();
          }

          if (position) {
            if (this.user.get('recentStatus.statusExpirationDate')) {
              userPin.innerHTML = `
                <i id='user-pin' class='fas fa-circle fa-2x'></i>
                <button
                  class='cm-Button cm-Button--3 user-status-actions isset'
                  cm-font-i='Button / 12 Medium'
                  cm-color-i='Neutral / White'
                  cm-background-i='Primary / CDLLife Blue'
                >
                  <span class="status-Icon">
                    <i class='fas fa-handshake-alt' cm-color='Neutral / White'></i>
                  </span>
                  <span class="status-placeholder">Edit my Meet & Greet</span>
                </button>
              `;
            } else {
              userPin.innerHTML = `
                <i id='user-pin' class='fas fa-circle fa-2x'></i>
                  <button
                    class='cm-Button cm-Button--3 user-status-actions unset'
                    cm-font-i='Button / 12 Medium'
                    cm-color-i='Neutral / Mine Shaft'
                    cm-background-i='Neutral / White'
                  >
                    <span class="status-Icon">
                      <i class='fas fa-handshake-alt' cm-color='Neutral / White'></i>
                    </span>
                    <span class="status-placeholder">Create a Meet & Greet</span>
                  </button>
              `;
            }

            this.userLocationMarker = new mapboxgl.Marker({
              element: userPin,
              anchor: 'bottom',
            })
              .setLngLat([position.longitude, position.latitude])
              .addTo(this.map);

            userPin.addEventListener('click', async e => {
              e.stopPropagation();
              const target = e.target as Element;
              this.log('CLICKED USER PIN');

              if (!(await this.userVerification.isVerified())) {
                return;
              }

              // if the pin clicked we want to display the users actions
              // We will also return because we only want to open the modal
              // when the user actions are clicked
              if (target.id && target.id == 'user-pin') {
                if ($('.user-status-actions').is(':hidden')) {
                  $('.user-status-actions').css('display', 'flex');
                }
                return;
              }
              if (this.user.get('recentStatus.statusExpirationDate')) {
                this.userStatusSet.emit(true);
              } else {
                if (this.checkAuthentication()) {
                  const modal = this.modalCtrl.create('StatusSettingsPage', {
                    editMode: false,
                  });
                  modal.onDidDismiss(statusChanged => {
                    this.expirationSet = !!this.user.get(
                      'recentStatus.statusExpirationDate'
                    );
                    this.updateUserPinHtml();
                    this.updateMeetGreetMarker(statusChanged);
                  });
                  modal.present();
                }
              }
            });

            $(this.userLocationMarker._element).css('z-index', 2);

            return position;
          }
        },
        e => {
          this.log(e);
        }
      );
    }
  }

  /**
   * Update the users pin HTML when the meet and greet modal is dismissed
   */
  updateUserPinHtml() {
    let html;

    if (this.user.get('userLocationPref') === 'nobody') {
      html = '';
    } else if (this.user.get('recentStatus.statusExpirationDate')) {
      html = `
        <i id='user-pin' class='fas fa-circle fa-2x'></i>
        <button
          class='cm-Button cm-Button--3 user-status-actions isset'
          cm-font-i='Button / 12 Medium'
          cm-color-i='Neutral / White'
          cm-background-i='Primary / CDLLife Blue'
        >
          <span class="status-Icon">
            <i class='fas fa-handshake-alt' cm-color='Neutral / White'></i>
          </span>
          <span class="status-placeholder">Edit my Meet & Greet</span>
        </button>
      `;
    } else {
      html = `
        <i id='user-pin' class='fas fa-circle fa-2x'></i>
          <button
            class='cm-Button cm-Button--3 user-status-actions unset'
            cm-font-i='Button / 12 Medium'
            cm-color-i='Neutral / Mine Shaft'
            cm-background-i='Neutral / White'
          >
            <span class="status-Icon">
              <i class='fas fa-handshake-alt' cm-color='Neutral / White'></i>
            </span>
            <span class="status-placeholder">Create a Meet & Greet</span>
          </button>
      `;
    }

    $('.user-pin-wrapper').html(html);
  }

  /**
   * Update the users location marker
   */
  updateUserLocationMarker() {
    this.geolocation.getRecentPosition().then(position => {
      if (!this.userLocationMarker) {
        return this.log('no marker');
      }

      this.userLocationMarker.setLngLat([
        position.longitude,
        position.latitude,
      ]);
    });
  }

  /**
   * Update the meet & greet location
   */
  updateMeetGreetMarker(statusChanged = false) {
    this.geolocation.getRecentPosition().then(position => {
      if (!statusChanged) return;
      let props = this.user.info,
        lng = _.get(props, 'recentStatus.nextLocation.nextLng'),
        lat = _.get(props, 'recentStatus.nextLocation.nextLat'),
        distanceAway;

      if (lng && lat) {
        distanceAway = turf.distance(
          turf.point([position.longitude, position.latitude]),
          turf.point([lng, lat]),
          { units: 'miles' }
        );
      }

      if (
        !this.user.get('recentStatus.statusExpirationDate') ||
        distanceAway <= 0.25
      ) {
        this.meetGreetMarker && this.meetGreetMarker.remove();
        this.centerMap(position.longitude, position.latitude);
      } else {
        this.createMeetGreetMarker(position.longitude, position.latitude);

        // only center the map if the meet&greet status has changed
        if (statusChanged)
          this.centerMap(lng || position.longitude, lat || position.latitude);
      }
    });

    this.tools.logAnalyticsEvent(
      'trackCustom',
      'discover-map-update-meetgreet-marker',
      {
        userId: this.user.get('_id'),
      }
    );
  }

  /**
   * Resize the map
   */
  resizeMapTimeouts() {
    if (this.map) {
      this.map.resize();
      setTimeout(() => {
        this.map && this.map.resize();
      }, 10);
      setTimeout(() => {
        this.map && this.map.resize();
      }, 20);
      setTimeout(() => {
        this.map && this.map.resize();
      }, 500);
      setTimeout(() => {
        this.map && this.map.resize();
      }, 2000);
    }
  }

  /**
   * Reconnect the active map
   */
  reconnectMap() {
    try {
      if (activeMap && activeMap !== this) {
        try {
          activeMap.clearMap();
        } catch (e) {}
      }
      if (DiscoverMapComponent.staticMapDiv) {
        this.discoverMapContainer.nativeElement.append(
          DiscoverMapComponent.staticMapDiv
        );
        _.set(this.map, 'activeComponent', this);
        activeMap = this;
      }
    } catch (e) {}
  }

  /**
   * Display error messages if the users location settings need to be changed (device specific)
   */
  displayErrorMessages() {
    this.errorMessage =
      'Uh Oh! It appears you have denied us access to your location.';
    this.isAndroidError =
      'To solve this, please go to the "Settings" App, click "Apps", scroll to CDLLife, click that, and then turn on location services. Once complete, you may need to restart the app.';
    this.isntAndroidError =
      'To solve this, please go to the "Settings" App, scroll to CDLLife, click that, and then turn on location services. Once complete, you may need to restart the app.';

    if (this.errorMessage) {
      this.tools.logAnalyticsEvent(
        'trackCustom',
        'discover-map-location-access-ERROR',
        {
          userId: this.user.get('_id'),
        }
      );
    }
  }

  /**
   * Returns the users meet&greet location if they have one set.
   * If there is no meet&greet then it defaults to their most
   * recent location
   * @param nextLoc users meet & greet location
   * @param position users current position
   * @returns meet&greet location || current position
   */
  checkMeetGreetLoc(nextLoc, position): Array<number> {
    if (nextLoc) {
      return [
        _.get(nextLoc, 'nextLng') || position.longitude,
        _.get(nextLoc, 'nextLat') || position.latitude,
      ];
    }

    return [position.longitude, position.latitude];
  }

  /**
   * Open the users app settings to change their location preference
   */
  openAppSettings() {
    try {
      const settings = _.get(window, 'cordova.plugins.settings');
      settings.open(
        'application_details',
        function () {
          // console.log('opened settings');
        },
        function () {
          // console.log('failed to open settings');
        }
      );
    } catch (e) {}
  }

  /**
   * Check if the user is authorized to view this page
   * @param key page to check
   */
  checkAuthentication(key?) {
    if (this.user.userPermission(key)) {
      return true;
    } else {
      this.tools.loginBlock('Create an account to get started.');
      return false;
    }
  }

  resizeAvatar(url) {
    return this.tools.resizeImage(url, 'width=100');
  }

  openMapListView(nearbyFriends?, nearbyUsers?, isCluster = false) {
    if (this.checkAuthentication()) {
      const modal = this.modalCtrl.create('FriendsListPage', {
        nearbyFriends: nearbyFriends || this.nearbyFriends,
        otherNearbyUsers: nearbyUsers || this.nearbyUsers,
        isCluster: isCluster,
      });
      modal.onDidDismiss(data => {
        this.log('friends list modal closed', data);
      });
      modal.present();
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/discover-map/discover-map.ts